"use client";

import React, { use } from "react";
import SectionBadge from "./SectionBadge";
import { TextAnimate } from "./TextAnimation";
import HowItWorksStepCard from "./HowItWorksStepCard";
import type { HowItWorksSectionData } from "@/types/cms";
import { useSectionTracking } from "@/lib/analytics/use-section-tracking";

interface HowItWorksSectionMobileProps {
  data?: HowItWorksSectionData;
}

export default function HowItWorksSectionMobile({
  data,
}: HowItWorksSectionMobileProps) {
  const badgeText = data?.badgeText || "How It Works";
  const title =
    data?.title ||
    "In a few simple steps, turn your attendee list into a fully automated matchmaking experience.";
  const steps = data?.steps || [];
  const sectionRef = useSectionTracking("How It Works Section", "how-it-works");
  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      id="how-it-works"
      className="bg-black py-16 px-4 lg:px-16 font-host-grotesk flex flex-col"
    >
      <div className="max-w-360 mx-auto flex flex-col items-center text-center mb-14">
        <SectionBadge text={badgeText} className="mb-12" />
        <TextAnimate
          animation="blurInUp"
          by="line"
          className="text-white font-host-grotesk text-4xl md:text-5xl lg:text-5xl"
          as="h2"
          duration={0.2}
          once={true}
        >
          {title}
        </TextAnimate>
      </div>
      <div className="relative max-w-330 gap-3 flex flex-col w-full mx-auto">
        {steps.length > 0 ? (
          steps.map((step, index) => {
            const lines = [step.descriptionLine1, step.descriptionLine2].filter(
              Boolean,
            ) as string[];
            return (
              <HowItWorksStepCard
                key={step.id}
                step={index + 1}
                title={step.title}
                lines={lines.length > 0 ? lines : [step.descriptionLine1]}
                imageSrc={
                  step.image?.url ||
                  `/landing-stuff/HowItsWork-${index + 1}.png`
                }
              />
            );
          })
        ) : (
          <>
            <HowItWorksStepCard
              step={1}
              title="Upload your attendee list"
              lines={[
                "Full name, email, and company name. That is all you need to start.",
              ]}
              imageSrc="/landing-stuff/HowItsWork-1.png"
            />
            <HowItWorksStepCard
              step={2}
              title="AI creates live profiles"
              lines={[
                "Our AI enriches each attendee with public information and builds dynamic profiles in real time.",
              ]}
              imageSrc="/landing-stuff/HowItsWork-2.png"
            />
            <HowItWorksStepCard
              step={3}
              title="Set access levels"
              lines={[
                "Define who can see and connect with whom across groups like speakers, exhibitors, and general attendees.",
              ]}
              imageSrc="/landing-stuff/HowItsWork-3.png"
            />
            <HowItWorksStepCard
              step={4}
              title="Launch your branded experience"
              lines={[
                "Distribute your web and mobile app under your conference brand and watch engagement rise.",
              ]}
              imageSrc="/landing-stuff/HowItsWork-4.png"
            />
          </>
        )}
      </div>
    </section>
  );
}
